import { motion } from 'framer-motion';

/**
 * Reusable empty-state primitive.
 *
 * Use for first-time/zero-data screens instead of blank space or a
 * lonely spinner. Surfaces the *next* action a user should take so a
 * new user is never staring at a void.
 *
 * Props:
 *  - icon: lucide-react component (rendered at 28px)
 *  - title: short headline
 *  - description: 1-2 line subtext
 *  - actionLabel + onAction: optional primary CTA
 *  - secondaryLabel + onSecondary: optional ghost CTA
 *  - testId: data-testid root (default 'empty-state')
 */
export default function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  testId = 'empty-state',
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-2xl border border-dashed"
      style={{
        borderColor: 'var(--isp-glass-border)',
        backgroundColor: 'var(--isp-glass-inner)',
      }}
      data-testid={testId}
    >
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-[#002FA7]/8 flex items-center justify-center mb-5">
          <Icon className="w-7 h-7 text-[#002FA7]" />
        </div>
      )}
      <h3
        className="text-lg font-semibold tracking-tight mb-1.5"
        style={{ color: 'var(--isp-heading)' }}
        data-testid={`${testId}-title`}
      >
        {title}
      </h3>
      {description && (
        <p className="text-sm max-w-md mb-6" style={{ color: 'var(--isp-body, #71717a)' }} data-testid={`${testId}-desc`}>
          {description}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {actionLabel && (
          <button
            onClick={onAction}
            className="bg-[#002FA7] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:opacity-90 transition"
            data-testid={`${testId}-action`}
          >
            {actionLabel}
          </button>
        )}
        {secondaryLabel && (
          <button
            onClick={onSecondary}
            className="text-sm font-medium px-3 py-2 hover:opacity-80"
            style={{ color: 'var(--isp-body, #52525b)' }}
            data-testid={`${testId}-secondary`}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </motion.div>
  );
}
